import { useMemo } from "react";

import { getTotalWeightFromGoodsItems } from "../../../../common/goods/goodsUtils";
import type { DetailedBooking } from "../../../../common/graphql/fragments/gql/DetailedBooking";
import type { UpdateSubmitInformation } from "../../../../common/update-information/utils/getUpdateSubmitInformation";
import { resolveAnimalsInformationText } from "../../../../common/utils/animalsUtils";
import { transformBookingToMinimalBooking } from "../../../../common/utils/minimalBooking";
import { shouldShowPluginInformationText } from "../../../../common/utils/temperatureUtil";
import { resolveArrivalNotepadApprovalInfo } from "../utils/arrivalNotepadInformation";
import { resolveAutoCancellationInformation } from "../utils/autoCancellationUtils";
import {
  getHazardousGoodsBannerApprovalInfo,
  getLoadingPreferenceApprovalInfo,
} from "../utils/detailsUtils";
import { resolveEnsCargoHintInformation } from "../utils/ensUpdateInformationUtils";
import { shouldShowHazGoodsPaymentInformation } from "../utils/showPaymentUtil";
import { BookingDetailsViewBanners } from "./BookingDetailsViewBanners";

interface BookingDetailsViewBannersContainerProps {
  booking: DetailedBooking;
  updateSubmitInformation: UpdateSubmitInformation;
}

export const BookingDetailsViewBannersContainer = ({
  booking,
  updateSubmitInformation,
}: BookingDetailsViewBannersContainerProps) => {
  const loadingPreferenceApprovalInfo = getLoadingPreferenceApprovalInfo(
    booking.loadingPreference,
    booking.loadingPreferenceStatusCode,
  );
  const hazardousGoodsWeight =
    booking.cargoWeight - getTotalWeightFromGoodsItems(booking.goods);

  // TODO: Checking booking.hazardousGoods here is just to comply with the weird approval info code that we want to remove
  const hazardousGoodsApprovalInfo = booking.hazardousGoods
    ? getHazardousGoodsBannerApprovalInfo(
        booking.hazardousGoods,
        booking.hazardousGoodsApproved,
        booking.bookingStatus.status,
        hazardousGoodsWeight,
      )
    : null;
  const livestockInfoText = resolveAnimalsInformationText(booking.livestock);
  const arrivalNotepadInfo = resolveArrivalNotepadApprovalInfo(
    booking.arrivalNotepadApplied,
  );
  const showHazGoodsPaymentInformation =
    shouldShowHazGoodsPaymentInformation(booking);
  const autoCancellation = resolveAutoCancellationInformation(
    updateSubmitInformation,
    booking.autoCancellation,
  );
  const minimalBooking = useMemo(
    () => transformBookingToMinimalBooking(booking),
    [booking],
  );

  const showPluginInformation = shouldShowPluginInformationText(
    booking.noOfPlugins,
    booking.pluginTemperature,
  );

  const showEnsGoodsBanner = resolveEnsCargoHintInformation(
    updateSubmitInformation,
    minimalBooking,
    booking.importReference.ensConfirmed,
  );

  if (
    !loadingPreferenceApprovalInfo &&
    !hazardousGoodsApprovalInfo &&
    !livestockInfoText &&
    !arrivalNotepadInfo &&
    !autoCancellation &&
    !showPluginInformation &&
    !showEnsGoodsBanner
  ) {
    return null;
  }

  return (
    <BookingDetailsViewBanners
      arrivalNotepadInfo={arrivalNotepadInfo}
      autoCancellation={autoCancellation}
      bookingNo={booking.bookingNo}
      hazardousGoodsApproved={booking.hazardousGoodsApproved}
      hazardousGoodsApprovalInfo={hazardousGoodsApprovalInfo}
      livestockInfoText={livestockInfoText}
      loadingPreferenceApprovalInfo={loadingPreferenceApprovalInfo}
      showHazGoodsPaymentInformation={showHazGoodsPaymentInformation}
      showPluginInformation={showPluginInformation}
      showEnsGoodsBanner={showEnsGoodsBanner}
    />
  );
};
